import { useGetJwtUtente } from '@framework/api-client/api/auth/jwt-controller/jwt-controller.ts';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { useHistory } from '@framework/ui/core/router/TypedRoute.tsx';
import { Alert, message } from 'antd';
import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { ClientConfigLoader } from '../client-config/ClientConfigLoader.tsx';
import { AuthError } from '../http/errors/AuthError.ts';
import { getNetworkErrorMessage } from '../http/errors/getNetworkErrorMessage.ts';
import {
  AuthActions,
  type IRootAuthState,
  useAuth,
  useCurrentUser,
} from '../state/AuthSlice.tsx';

export interface IAuthCurrentLoaderProps {
  children: React.ReactNode;
}

const SkipHttpStatuses = [400, 401, 403];

/**
 * Caricamento dell'utente corrente. Serve anche per controllare come side
 * effect se l'utente è loggato, in quanto i token sono salvati su
 * cookie HTTP-Only e non sono quindi accessibili da JS.
 */
export function AuthCurrentLoader(props: IAuthCurrentLoaderProps) {
  const dispatch = useDispatch();
  const store = useStore<IRootAuthState>();
  const { isLogged, isPrelogged, fetchCount } = useAuth(); // TODO prelogged
  const user = useCurrentUser();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null as any);
  const [errorType, setErrorType] = useState('error' as 'error' | 'info');

  const session = useGetJwtUtente({
    query: {
      enabled: false,
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <tbd>
  useEffect(() => {
    let isMounted = true;

    const fetchCurrent = async () => {
      console.log('[AuthCurrentLoader]', { isLogged });

      // Attualmente non supportiamo il refresh del token in questa
      // fase del caricamento dell'utente.
      const { accessToken, refreshToken } = store.getState().auth;
      if (!accessToken && !refreshToken) {
        dispatch(AuthActions.logout());
        return;
      }

      try {
        setLoading(true);
        const result = await session.refetch({ throwOnError: true });
        console.log('[AuthCurrentLoader] Refreshed user');
        dispatch(AuthActions.currentLoaded({ current: result.data! }));
      } catch (e: any) {
        // Gestiamo il caso in cui il componente è stato ricaricato
        if (!isMounted) return;

        if (
          (e?.response && SkipHttpStatuses.includes(e.response.status)) ||
          e instanceof AuthError
        ) {
          console.log(`[AuthCurrentLoader] Auth Error`, e);
          // biome-ignore format: <tbd>
          message.error(`La sessione non è valida, si prega di autenticarsi nuovamente.`);
          dispatch(AuthActions.logout());
        } else {
          // biome-ignore format: <tbd>
          console.error("[AuthCurrentLoader] Errore sconosciuto durante il caricamento dell'utente", e);
          setErrorType('error');
          setError(
            getNetworkErrorMessage(
              e,
              "Si è verificato un errore durante il caricamento dell'utente.",
            ),
          );
        }

        // In caso ci sia un errore in fase di caricamento dell'utente,
        // potrebbe avvenire un redirect automatico in loop, se c'è il sistema
        // di auto-login con IdP.
        const queryParams = new URLSearchParams(history.location.search);
        if (queryParams.has('idp')) {
          console.log(`[AuthCurrentLoader] Removing idp from URL`);
          queryParams.delete('idp');
          history.replace({
            pathname: history.location.pathname,
            search: queryParams.toString(),
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (isLogged || isPrelogged) {
      fetchCurrent();
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [isLogged, isPrelogged, fetchCount]);

  // Per evitare caricamenti a vuoto, mostriamo il loader
  if ((isPrelogged && !user && !error) || loading) return <LoaderSpin full />;

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message={"Impossibie caricare l'utente"}
        description={error}
      />
      // TODO Inserire NetworkAlertPage
      // <NetworkAlertPage message="Impossibile caricare l'utente" error={error} />
    );
  }

  return <ClientConfigLoader>{props.children}</ClientConfigLoader>;
}
